import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import api from '../../utils/axiosConfig';
import { getImageUrl } from '../../utils/imageUtils';

interface Project {
    _id: string;
    title: string;
    slug: string;
    subtitle?: string;
    category: string;
    shortDescription: string;
    image?: string;
    logo?: string;
    banner?: string;
    color: string;
}

const Projects: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await api.get('/projects');
            setProjects(response.data.data || []);
        } catch (error: any) {
            console.error('Failed to fetch projects:', error);
        } finally {
            setLoading(false);
        }
    };

    const getProjectImage = (project: Project) => {
        if (project.image) return getImageUrl(project.image);
        if (project.banner) return getImageUrl(project.banner);
        if (project.logo) return getImageUrl(project.logo);
        // Use placeholder images based on project color
        if (project.color === 'blue') {
            return 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop';
        } else if (project.color === 'orange') {
            return 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop';
        }
        return 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop';
    };

    const getBadgeColor = (color: string) => {
        switch (color) {
            case 'blue': return 'bg-gradient-to-r from-[#3B82F6] to-blue-600';
            case 'orange': return 'bg-gradient-to-r from-[#F59E0B] to-orange-600';
            case 'green': return 'bg-gradient-to-r from-green-500 to-green-600';
            case 'purple': return 'bg-gradient-to-r from-purple-500 to-purple-600';
            default: return 'bg-gradient-to-r from-gray-500 to-gray-600';
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <PublicHeader />

            <section className="pt-32 pb-24 bg-gradient-to-br from-blue-50/40 via-white to-orange-50/30 relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl"></div>
                    <div className="absolute top-60 -left-40 w-96 h-96 bg-[#F59E0B]/10 rounded-full blur-3xl"></div>
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-5xl mx-auto text-center">
                        <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-8">
                            <span className="w-2 h-2 bg-[#3B82F6] rounded-full mr-2 animate-pulse"></span>
                            <span className="text-sm font-medium text-[#3B82F6]">Our Portfolio</span>
                        </div>
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-8 leading-tight">
                            <span className="text-[#3B3B3B] inline">Our Latest </span>
                            <span className="text-[#3B82F6] inline bg-gradient-to-r from-[#3B82F6] to-blue-500 bg-clip-text text-transparent">Projects</span>
                        </h1>
                        <p className="text-xl sm:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                            Discover our recent successful campaigns and transformations
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="spinner mx-auto"></div>
                            <p className="mt-4 text-gray-600">Loading projects...</p>
                        </div>
                    ) : projects.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-600">No projects available at the moment.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                            {projects.map((project) => (
                                <div key={project._id} className="group bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                                    <div className="relative h-48 overflow-hidden">
                                        <img
                                            src={getProjectImage(project)}
                                            alt={project.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                        />
                                        <div className="absolute top-4 left-4">
                                            <span className={`inline-block px-3 py-1 ${getBadgeColor(project.color)} text-white rounded-full text-xs font-semibold shadow-lg`}>
                                                {project.category}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold text-[#3B3B3B] mb-3 group-hover:text-[#3B82F6] transition-colors">
                                            {project.title}
                                        </h3>
                                        <p className="text-base text-gray-600 mb-6 leading-relaxed line-clamp-3">
                                            {project.shortDescription}
                                        </p>
                                        <Link
                                            to={`/projects/${project.slug}`}
                                            className="inline-flex items-center px-6 py-3 bg-[#3B82F6] text-white rounded-xl hover:bg-blue-700 transition-all duration-300 font-semibold text-sm group-hover:gap-2"
                                        >
                                            View More
                                            <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="text-center">
                        <Link
                            to="/contact"
                            className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold text-base"
                        >
                            Start Your Project
                        </Link>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
};

export default Projects;


